import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";

import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import DynamicOtherLiabilities from "app/components/Individual/applicant/assetsLiabilities/DynamicOtherLiabilities";
import { handleApiError } from "app/utils/handleApiError";

interface Inter {
  name: string;
  balanceOwing: string;
  minMonthlyPayment: string;
  dbId?: string;
}

export interface AssetsLiabilitiesPartFourFormData {
  otherliabilitiesType: string;
  otherLiabilities: Inter[];
}

const PartFour: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const { mutate: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formFour, assetLiabilityId, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  ) || {
    formFour: { otherLiabilities: [] },
  };
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<AssetsLiabilitiesPartFourFormData>({
    defaultValues: {
      otherliabilitiesType: "no",
      otherLiabilities: [
        {
          name: "",
          balanceOwing: "",
          minMonthlyPayment: "",
        },
      ],
    },
  });
  const otherliabilitiesValue = watch("otherliabilitiesType");

  const onSubmit = (data: AssetsLiabilitiesPartFourFormData) => {
    if (!savedIds?.applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const { otherliabilitiesType, otherLiabilities } = data || {};
    const payload: any = {};

    if (otherliabilitiesType === "yes") {
      payload.otherLiabilities = otherLiabilities.map((item) => ({
        id: item.dbId,
        name: item.name,
        minMonthlyPayment: Number(item.minMonthlyPayment),
        balanceOwing: Number(item.balanceOwing),
      }));
    } else {
      payload.otherLiabilities = null;
    }

    submitAssetsLiabilities(
      {
        url: `${ASSST_LIABILITY}/${savedIds?.applicantID}/fifth`,
        data: payload,
      },
      {
        onSuccess: (data: any) => {
          const { otherLiabilities } = data || {};
          const otherLiabilitiesMapped = otherLiabilities?.map((item: any) => ({
            dbId: item.id,
            name: item.name,
            balanceOwing: item.balanceOwing,
            minMonthlyPayment: item.minMonthlyPayment,
          }));

          dispatch(
            addStepsData({
              ...others,
              formFour: {
                ...formFour,
                otherLiabilities:
                  otherliabilitiesType === "no"
                    ? [
                        {
                          name: "",
                          balanceOwing: "",
                          minMonthlyPayment: "",
                        },
                      ]
                    : otherLiabilitiesMapped,
                otherliabilitiesType,
              },
            })
          );
          dispatch(nextStep());
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "otherLiabilities",
  });

  useEffect(() => {
    if (formFour && formFour?.otherliabilitiesType) {
      reset({
        otherLiabilities:
          formFour.otherliabilitiesType === "no"
            ? [{ name: "", balanceOwing: "", minMonthlyPayment: "" }]
            : formFour.otherLiabilities,
        ...(formFour.otherliabilitiesType && {
          otherliabilitiesType: formFour.otherliabilitiesType,
        }),
      });
    }
  }, [formFour, reset]);

  return (
    <>
      <ProgressBar progress={48} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Other Liabilities" />
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any other liabilities ?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="otherliabilitiesType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="otherliabilities_yes"
                      name="otherliabilitiesType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="otherliabilities_no"
                      name="otherliabilitiesType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.otherliabilitiesType && (
              <ErrorsMessage
                title={errors.otherliabilitiesType.message}
                className="!text-left"
              />
            )}
          </div>

          {otherliabilitiesValue === "yes" && (
            <>
              <DynamicOtherLiabilities
                control={control}
                errors={errors}
                fields={fields}
                deleteItem={deleteItem}
                otherLiabilities="otherLiabilities"
                remove={remove}
                applicantID={savedIds?.applicantID as string}
                deleteIsPending={deleteIsPending}
              />
              {fields.length < 3 && (
                <div className="flex items-center justify-end mt-6">
                  <AddMore
                    onClick={() =>
                      append({
                        name: "",
                        balanceOwing: "",
                        minMonthlyPayment: "",
                      })
                    }
                  />
                </div>
              )}
            </>
          )}

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartFour;
